import {
  CSSProperties,
  KeyboardEvent as ReactKeyboardEvent,
  PointerEvent as ReactPointerEvent,
  ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";

import IPagedRequest from "../../models/dto/fetch/IPagedRequest";
import { REDESIGN_NS } from "../i18n";
import { IconExport, IconSearch, IconSort } from "./icons";
import Select from "./Select";

/** `sorting` paylaşılan `IPagedRequest`'te yok; Smart frontend'inin karşılığında var. */
export type DataTableRequest = IPagedRequest & { sorting?: string };

export interface DataTableColumn<T> {
  key: string;
  header: string;
  render: (row: T) => ReactNode;
  width?: number;
  /** Sunucuya gidecek alan adı; `true` ise `key` gönderilir. */
  sortable?: boolean | string | string[];
  searchable?: boolean;
  /** Kolon aramasını metin kutusu yerine seçim listesi yapar. */
  searchOptions?: { value: string; label: string }[];
}

interface Props<T> {
  columns: DataTableColumn<T>[];
  data: T[];
  totalCount: number;
  isLoading: boolean;
  fetchData?: (params: DataTableRequest) => any;
  rowKey: (row: T) => string | number;
  /** Değişince tablo 1. sayfadan yeniden yüklenir: store mutasyondan sonra ilk sayfayı
      kendisi çekiyor, tablo hizalanmazsa veri ile sayfa numarası ayrışır. */
  reloadToken?: number;
  /** `sorting`: ABP tarzı tek string; varsayılan `orderBy` + `orderType`. */
  sortMode?: "orderBy" | "sorting";
  /** Dosyayı üreten taraf çağıran koddur; bileşen yalnızca sorgu parametrelerini verir. */
  onExport?: (params: DataTableRequest) => void;
  bordered?: boolean;
}

const PAGE_SIZES = [5, 10, 25, 50];

const SEARCH_DEBOUNCE_MS = 400;

const MIN_PANEL_HEIGHT = 260;

const RESIZE_STEP = 32;

/*
 * Band dar olmak zorunda: panel ekrana oturduğunda tutamak dibe zaten ~27px uzakta
 * duruyor, geniş bir band basar basmaz büyümeyi tetikliyordu. Hız saniye başına (kare
 * başına olsaydı 144Hz'de iki kat hızlı olurdu) ve bandın üst kenarında sıfır değil:
 * sıfırdan başlayan rampa "fareyi dibe dayadım, bir şey olmuyor" hissi veriyordu.
 */
const EDGE_ZONE = 20;
const EDGE_SPEED_MIN = 150;
const EDGE_SPEED_MAX = 700;

/** Sekme arkaplandayken biriken süre tek karede sıçramaya dönüşmesin. */
const MAX_FRAME_MS = 50;

/* Panel sayfayı bu kadar geçince otomatik büyüme durur: sayfa artık kayamıyor demektir
   ve panel büyümeye devam ederse tutamak imlecin altından kaçar. */
const MAX_SCROLL_LAG = 24;

interface SortState {
  key: string;
  desc: boolean;
}

interface QueryState {
  page: number;
  pageSize: number;
  search: string;
  columns: Record<string, string>;
  sort: SortState | null;
}

/**
 * Sunucu taraflı sayfalanan tablo. `components/Table`ın markup'ı Bootstrap'e bağlı
 * olduğu için kullanılamadı; istek/yanıt sözleşmesi ondan aynen korundu.
 */
function DataTable<T>({
  columns,
  data,
  totalCount,
  isLoading,
  fetchData,
  rowKey,
  reloadToken = 0,
  sortMode = "orderBy",
  onExport,
  bordered = false,
}: Props<T>) {
  const { t } = useTranslation(REDESIGN_NS);

  // Kutuya yazılan ham değer istekten ayrı: input her tuşta, sorgu yazma durunca tazelenir.
  const [searchText, setSearchText] = useState("");
  const [columnSearchText, setColumnSearchText] = useState<Record<string, string>>({});

  /** İsteği belirleyen her şey tek state'te: React 17 `setTimeout` içindeki
      güncellemeleri batch'lemediği için ayrı state'ler mükerrer istek üretiyordu. */
  const [query, setQuery] = useState<QueryState>({
    page: 0,
    pageSize: PAGE_SIZES[0],
    search: "",
    columns: {},
    sort: null,
  });
  const { page, pageSize, sort } = query;

  const lastReloadToken = useRef(reloadToken);
  /** Dışa aktarma, tabloyu dolduran sorgunun aynısıyla çağrılır. */
  const lastParams = useRef<DataTableRequest>({ skipCount: 0, maxResultCount: pageSize });

  const fetchRef = useRef(fetchData);
  fetchRef.current = fetchData;
  const isFetching = useRef(false);
  const queued = useRef<DataTableRequest | null>(null);

  /**
   * Aynı anda tek istek uçurur; sürerken gelen parametre kuyruğa alınır, böylece geç
   * dönen eski bir cevap yenisini ezemez.
   */
  const run = useCallback((params: DataTableRequest) => {
    lastParams.current = params;
    if (!fetchRef.current) return;

    if (isFetching.current) {
      queued.current = params;
      return;
    }

    isFetching.current = true;
    Promise.resolve(fetchRef.current(params))
      .catch(() => undefined)
      .then(() => {
        isFetching.current = false;
        const next = queued.current;
        queued.current = null;
        if (next) run(next);
      });
  }, []);

  useEffect(() => {
    if (filterKey(searchText, columnSearchText) === filterKey(query.search, query.columns))
      return;

    const timer = window.setTimeout(() => {
      setQuery((current) => ({
        ...current,
        page: 0,
        search: searchText,
        columns: columnSearchText,
      }));
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [searchText, columnSearchText, query.search, query.columns]);

  useEffect(() => {
    // Sayfa 1'de değilsek önce sayfayı sıfırla: değişim bu effect'i yeniden tetikler.
    if (lastReloadToken.current !== reloadToken) {
      lastReloadToken.current = reloadToken;
      if (query.page !== 0) {
        setQuery((current) => ({ ...current, page: 0 }));
        return;
      }
    }

    const params: DataTableRequest = {
      skipCount: query.page * query.pageSize,
      maxResultCount: query.pageSize,
    };

    if (query.sort) {
      const column = columns.find((c) => c.key === query.sort!.key);
      const type = query.sort.desc ? "desc" : "asc";
      const field = sortField(column, query.sort.key);

      if (sortMode === "sorting") {
        params.sorting = Array.isArray(field)
          ? field.map((f) => `${f} ${type}`).join(",")
          : `${field} ${type}`;
      } else {
        params.orderBy = Array.isArray(field) ? field[0] : field;
        params.orderType = type;
      }
    }

    if (query.search) params.searchText = query.search;

    const individual = Object.keys(query.columns)
      .filter((name) => query.columns[name])
      .map((name) => ({ name, search: query.columns[name] }));
    if (individual.length > 0) params.columns = individual;

    run(params);
    // columns'ın kimliği her render'da değişiyor; bağımlılığa alınırsa istek döngüye girer.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, reloadToken, sortMode, run]);

  const pageCount = Math.max(1, Math.ceil(totalCount / pageSize));

  const onPageChange = (value: number) =>
    setQuery((current) => ({ ...current, page: value }));

  const onPageSizeChange = (value: number) =>
    setQuery((current) => ({ ...current, pageSize: value, page: 0 }));

  const toggleSort = (key: string) =>
    setQuery((current) => {
      const previous = current.sort;
      let next: SortState | null;
      if (!previous || previous.key !== key) next = { key, desc: false };
      else if (!previous.desc) next = { key, desc: true };
      else next = null;
      return { ...current, page: 0, sort: next };
    });

  const onColumnSearchChange = (key: string, value: string) => {
    setColumnSearchText((current) => ({ ...current, [key]: value }));
  };

  const pages = useMemo(() => buildPageList(page + 1, pageCount), [page, pageCount]);

  const panelRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  /** Ekrana sığdıran otomatik sınır; ölçülür, sabit değildir. */
  const [autoMax, setAutoMax] = useState<number>();
  /** Kullanıcı tutamağı sürüklediyse geçerli olan yükseklik; sınırı devre dışı bırakır. */
  const [height, setHeight] = useState<number>();

  /*
   * Sabit bir `100vh - N` yazılamıyor: panelin üstünde kalan yükseklik sayfadan sayfaya
   * değişiyor. Panelin belge içindeki konumu ölçülüp kalan yer sınır yapılıyor.
   */
  useLayoutEffect(() => {
    let cancelled = false;

    const measure = () => {
      const panel = panelRef.current;
      if (cancelled || !panel) return;

      const style = window.getComputedStyle(panel);
      const gap = parseFloat(style.marginBottom) || 0;
      const top = panel.getBoundingClientRect().top + window.pageYOffset;

      setAutoMax(Math.max(MIN_PANEL_HEIGHT, window.innerHeight - top - gap));
    };

    measure();
    window.addEventListener("resize", measure);

    // Font `swap` ile geliyor; asıl font oturunca sekme bandının boyu değişebiliyor.
    document.fonts?.ready.then(measure).catch(() => undefined);

    return () => {
      cancelled = true;
      window.removeEventListener("resize", measure);
    };
  }, []);

  /*
   * İçeriğin tamamının göründüğü yükseklik; sürüklemenin üst sınırı budur, yani tutamak
   * gösterilecek satır kalmadığı yerde durur.
   */
  const contentHeight = useCallback(() => {
    const panel = panelRef.current;
    const scroll = scrollRef.current;
    if (!panel || !scroll) return MIN_PANEL_HEIGHT;
    return panel.offsetHeight + (scroll.scrollHeight - scroll.clientHeight);
  }, []);

  const drag = useRef<{
    startDocY: number;
    startHeight: number;
    max: number;
    /** Kenarda beklerken biriken otomatik büyüme; imleç hareketinden bağımsızdır. */
    boost: number;
    /** Basıldığı andaki ekran koordinatı; kenar bandı yalnızca bunun altında çalışır. */
    startClientY: number;
    /** Panelin büyüyüp de sayfanın kaydırılamadığı, yani biriken fark. */
    lag: number;
    /** Payın üst sınırı: sürükleme başlarken sayfanın yukarı kaydırılabildiği kadar. */
    maxSlack: number;
    /** Bir önceki karede uygulanan pay; sayfanın geri kaydırılması buna bakar. */
    slack: number;
    /** Bir önceki karenin zaman damgası; büyüme hızı geçen süreyle ölçeklenir. */
    lastFrame?: number;
  } | null>(null);
  /*
   * Kısaltırken panelin altına konan pay ve onu tutan ara parça.
   *
   * Sayfa dibe dayalıyken tutamağın ekrandaki yeri `ekran boyu - alt boşluk`, yani panelin
   * yüksekliğinden bağımsız: panel kısalırken belge de kısalıyor, tarayıcı kaydırmayı
   * yukarı çekiyor ve iki hareket birbirini götürüyor, tutamak yerinde kalıyordu. Pay bu
   * kilidi açıyor ve ayarlanabilir bir katsayısı yok: tutamağın birebir takip etmesi payın
   * imleç yolu kadar olmasını şart koşuyor, geriye kalan tek serbestlik yükseklik ve o
   * zorunlu olarak imlecin İKİ KATI kadar kısalıyor.
   */
  const [slack, setSlack] = useState(0);
  const [isResizing, setIsResizing] = useState(false);
  const slackRef = useRef<HTMLDivElement>(null);

  /** Son imleç konumu; otomatik kaydırma fare durduğunda da buna bakar. */
  const pointerY = useRef(0);
  const autoScroll = useRef<number>();
  const savedScrollBehavior = useRef<string>();

  /** Uygulanan son yükseklik; `setHeight` eşzamanlı okunamadığı için ayrıca tutulur. */
  const heightRef = useRef<number>();

  /*
   * Fark BELGE koordinatında hesaplanır: sürükleme sırasında sayfa kayarsa fark
   * kendiliğinden büyür, yani tekerlek de tutamağı uzatmanın bir yolu olur. Yükseklik
   * DOM'a doğrudan yazılır çünkü `setHeight` bir sonraki render'da uygulanıyor, oysa
   * `scrollBy` hemen ardından çağrılıyor ve belge o an hâlâ eski boyunda.
   */
  const applyHeight = useCallback(() => {
    const state = drag.current;
    if (!state) return;
    const scrollBefore = window.pageYOffset;
    const docY = pointerY.current + scrollBefore;
    /* Tam piksele yuvarlanır: küsurat birikip `lag` sayacını şişiriyor ve büyümeyi içerik
       bitmeden durduruyordu. */
    const delta = docY - state.startDocY + state.boost;

    /*
     * Pay yalnızca kısaltmada var: büyütmede belge zaten kaydırma yeri üretiyor. Sayfanın
     * yukarı kaydırılabildiği kadarla sınırlı.
     */
    const rawSlack = Math.min(Math.max(0, -delta), state.maxSlack);
    /*
     * Panel + pay toplamı, belgenin imleç yolu kadar kısalması için tutulması gereken değer;
     * pay bundan artakalan, böylece panel alt sınırına dayanınca pay da durur.
     */
    const total = Math.max(MIN_PANEL_HEIGHT, state.startHeight + delta);
    const next = Math.round(clamp(total - rawSlack, state.max));
    const nextSlack = Math.round(Math.min(rawSlack, Math.max(0, total - next)));

    heightRef.current = next;
    const panel = panelRef.current;
    if (panel) {
      panel.style.maxHeight = "none";
      panel.style.height = next + "px";
    }
    setHeight(next);

    /*
     * Pay aynı karede DOM'a yazılır; `setSlack` bir sonraki render'a kalırsa tarayıcı
     * aradaki karede kaydırmayı fazladan çeker ve tutamak zıplar.
     */
    if (slackRef.current) slackRef.current.style.height = nextSlack + "px";
    setSlack(nextSlack);

    /*
     * Pay çözülürken sayfa geri kaydırılır: kısaltmada bunu tarayıcı kendisi yapıyor,
     * büyütmede kimse yapmıyordu ve panel imlecin iki katı hızla açılıyordu.
     */
    if (nextSlack < state.slack) window.scrollBy(0, state.slack - nextSlack);
    state.slack = nextSlack;

    /*
     * Panel küçülünce belge kısalır ve sayfa dibe dayalıysa tarayıcı kaydırmayı yukarı
     * çeker; bu düşüş `startDocY`ye işlenmezse imleç farkı sayılıp panel bir kez daha
     * küçülür ve tutamak çöker.
     */
    const scrollAfter = window.pageYOffset;
    if (scrollAfter !== scrollBefore) {
      state.startDocY += scrollAfter - scrollBefore;
    }
  }, []);

  /*
   * İmleç ekranın alt/üst kenarında beklerken panel kendiliğinden büyümeye devam eder.
   * Sıra önemli: ÖNCE panel büyür, sayfa büyüdüğü kadar kaydırılır. Tersi kilitleniyordu,
   * çünkü panel ekrana tam oturduğunda belgede kaydırılacak piksel kalmıyor. Kendi yaptığı
   * kaydırma `startDocY`den düşülür, tekerleğinki düşülmez (onun paneli uzatması istenen
   * davranış). Band yalnızca imleç başlangıç noktasını geçince çalışır ve biriken fark
   * `MAX_SCROLL_LAG`i aşarsa büyüme durur.
   */
  const autoScrollStep = useCallback((time: number) => {
    const state = drag.current;
    if (!state) return;

    const elapsed =
      state.lastFrame === undefined
        ? 0
        : Math.min(MAX_FRAME_MS, time - state.lastFrame);
    state.lastFrame = time;

    const y = pointerY.current;
    const toBottom = window.innerHeight - y;
    const current = heightRef.current ?? 0;
    let delta = 0;

    if (
      toBottom < EDGE_ZONE &&
      y > state.startClientY &&
      current < state.max &&
      state.lag < MAX_SCROLL_LAG
    ) {
      delta = (edgeSpeed(toBottom) * elapsed) / 1000;
    } else if (
      y < EDGE_ZONE &&
      y < state.startClientY &&
      current > MIN_PANEL_HEIGHT
    ) {
      delta = (-edgeSpeed(y) * elapsed) / 1000;
    }

    if (delta !== 0) {
      state.boost += delta;
      applyHeight();

      const grew = (heightRef.current ?? 0) - current;
      if (grew !== 0) {
        const scrollBefore = window.pageYOffset;
        window.scrollBy(0, grew);
        const scrolled = window.pageYOffset - scrollBefore;
        state.startDocY += scrolled;
        // Yalnızca aşağı büyümede anlamlı; yukarı küçülmede fark zaten kapanıyor.
        state.lag = Math.max(0, state.lag + grew - scrolled);
      }
    }

    autoScroll.current = window.requestAnimationFrame(autoScrollStep);
  }, [applyHeight]);

  const stopDrag = useCallback(() => {
    drag.current = null;
    if (savedScrollBehavior.current !== undefined) {
      document.documentElement.style.scrollBehavior = savedScrollBehavior.current;
      savedScrollBehavior.current = undefined;
    }
    if (autoScroll.current !== undefined) {
      window.cancelAnimationFrame(autoScroll.current);
      autoScroll.current = undefined;
    }
    window.removeEventListener("scroll", applyHeight);
    window.removeEventListener("pointerup", stopDrag);
    window.removeEventListener("pointercancel", stopDrag);

    /*
     * Pay sıfırlanınca belge kısalır ve sayfa aynı kadar yukarı oturur: panel ekranın
     * dibine gelir, boyu değişmez. Bırakma anında görülen boy nihai boy.
     */
    setIsResizing(false);
    setSlack(0);
  }, [applyHeight]);

  const onResizeDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    const panel = panelRef.current;
    if (!panel || event.button !== 0) return;
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);

    pointerY.current = event.clientY;
    heightRef.current = panel.offsetHeight;
    const contentMax = contentHeight();
    drag.current = {
      startDocY: event.clientY + window.pageYOffset,
      startClientY: event.clientY,
      startHeight: panel.offsetHeight,
      max: contentMax,
      boost: 0,
      lag: 0,
      maxSlack: window.pageYOffset,
      slack: 0,
    };

    // Ara parça sürükleme boyunca geçişsiz: geçiş uygulanırsa pay imlecin gerisinde kalır.
    setIsResizing(true);
    setSlack(0);

    /*
     * Bootstrap reboot `:root { scroll-behavior: smooth }` tanımlıyor. Açıkken `scrollBy`
     * anında kaydırmıyor, animasyon başlatıyor: hemen sonra okunan `pageYOffset` eski değeri
     * veriyor ve "ne kadar kaydırabildim" ölçümü hep 0 çıkıyor.
     */
    const root = document.documentElement;
    savedScrollBehavior.current = root.style.scrollBehavior;
    root.style.scrollBehavior = "auto";

    /*
     * Tutamağın kendi `pointerup`ı kaçabiliyor (imleç yakalaması düşerse olay başka öğeye
     * gider) ve o durumda sürükleme hiç bitmiyor; bırakma bu yüzden `window`dan da dinlenir.
     */
    window.addEventListener("pointerup", stopDrag);
    window.addEventListener("pointercancel", stopDrag);

    // Tekerlekle kaydırmada `pointermove` gelmez; yüksekliği kaydırma olayı tazeler.
    window.addEventListener("scroll", applyHeight);
    autoScroll.current = window.requestAnimationFrame(autoScrollStep);
  };

  const onResizeMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!drag.current) return;
    pointerY.current = event.clientY;
    applyHeight();
  };

  const onResizeUp = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!drag.current) return;
    stopDrag();
    event.currentTarget.releasePointerCapture(event.pointerId);
  };

  useEffect(
    () => () => {
      // Sökülürken `stopDrag` çağrılmaz: o React durumuna yazıyor.
      drag.current = null;
      if (autoScroll.current !== undefined) {
        window.cancelAnimationFrame(autoScroll.current);
      }
      window.removeEventListener("scroll", applyHeight);
      window.removeEventListener("pointerup", stopDrag);
      window.removeEventListener("pointercancel", stopDrag);
      if (savedScrollBehavior.current !== undefined) {
        document.documentElement.style.scrollBehavior = savedScrollBehavior.current;
      }
    },
    [applyHeight, stopDrag]
  );

  const onResizeKey = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    const panel = panelRef.current;
    if (!panel) return;

    const step =
      event.key === "ArrowDown"
        ? RESIZE_STEP
        : event.key === "ArrowUp"
        ? -RESIZE_STEP
        : 0;
    if (step === 0) return;

    event.preventDefault();
    const next = clamp(panel.offsetHeight + step, contentHeight());
    heightRef.current = next;
    setHeight(next);
  };

  const currentHeight = height ?? autoMax ?? MIN_PANEL_HEIGHT;

  // Elle ayarlandığında `max-height` kaldırılır: taşan bir yükseklik kullanıcının kararı.
  const panelStyle: CSSProperties =
    height !== undefined ? { height, maxHeight: "none" } : { maxHeight: autoMax };

  return (
    <>
      <div className="sso-rd__panel" ref={panelRef} style={panelStyle}>
        <div className="sso-rd__panel-top">
          <div className="sso-rd__panel-top-left">
            {onExport && (
              <button
                type="button"
                className="sso-rd__btn sso-rd__btn--modal sso-rd__btn--secondary"
                onClick={() => onExport(lastParams.current)}
              >
                <IconExport size={16} />
                {t("Excel")}
              </button>
            )}
          </div>

          <div className="sso-rd__search">
            <IconSearch size={17} />
            <input
              type="text"
              value={searchText}
              placeholder={t("Search in records...")}
              onChange={(event) => setSearchText(event.target.value)}
            />
          </div>
        </div>

        <div className="sso-rd__table-scroll" ref={scrollRef}>
          <table
            className={"sso-rd__table" + (bordered ? " sso-rd__table--bordered" : "")}
          >
            <thead>
              <tr>
                {columns.map((column) => {
                  const isSorted = sort?.key === column.key;
                  return (
                    <th
                      key={column.key}
                      style={column.width ? { width: column.width } : undefined}
                      aria-sort={
                        isSorted ? (sort!.desc ? "descending" : "ascending") : undefined
                      }
                    >
                      {column.sortable ? (
                        <button
                          type="button"
                          className={
                            "sso-rd__th-sort" + (isSorted ? " is-active" : "")
                          }
                          onClick={() => toggleSort(column.key)}
                        >
                          {column.header}
                          <IconSort
                            size={12}
                            direction={isSorted ? (sort!.desc ? "desc" : "asc") : null}
                          />
                        </button>
                      ) : (
                        column.header
                      )}

                      {column.searchable && (
                        <div className="sso-rd__col-search">
                          {column.searchOptions ? (
                            <Select
                              variant="col"
                              value={columnSearchText[column.key] || ""}
                              onChange={(next) =>
                                onColumnSearchChange(column.key, next)
                              }
                              options={[
                                { value: "", label: t("All") },
                                ...column.searchOptions,
                              ]}
                              ariaLabel={column.header}
                            />
                          ) : (
                            <input
                              type="text"
                              className="sso-rd__col-input"
                              value={columnSearchText[column.key] || ""}
                              placeholder={column.header}
                              aria-label={column.header}
                              onChange={(event) =>
                                onColumnSearchChange(column.key, event.target.value)
                              }
                            />
                          )}
                        </div>
                      )}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={columns.length}>
                    <div className="sso-rd__table-state">{t("Loading")}...</div>
                  </td>
                </tr>
              ) : data.length === 0 ? (
                <tr>
                  <td colSpan={columns.length}>
                    <div className="sso-rd__table-state">
                      {t("There are no records found.")}
                    </div>
                  </td>
                </tr>
              ) : (
                data.map((row) => (
                  <tr key={rowKey(row)}>
                    {columns.map((column) => (
                      <td key={column.key}>{column.render(row)}</td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="sso-rd__pagination">
          <div className="sso-rd__pages">
            <button
              type="button"
              className="sso-rd__page-btn"
              onClick={() => onPageChange(page - 1)}
              disabled={page === 0}
              title={t("Previous page")}
              aria-label={t("Previous page")}
            >
              &laquo;
            </button>

            {pages.map((item, index) =>
              item === null ? (
                <span className="sso-rd__page-gap" key={"gap-" + index}>
                  …
                </span>
              ) : (
                <button
                  type="button"
                  key={item}
                  className={
                    "sso-rd__page-btn" + (item === page + 1 ? " is-active" : "")
                  }
                  onClick={() => onPageChange(item - 1)}
                >
                  {item}
                </button>
              )
            )}

            <button
              type="button"
              className="sso-rd__page-btn"
              onClick={() => onPageChange(page + 1)}
              disabled={page + 1 >= pageCount}
              title={t("Next page")}
              aria-label={t("Next page")}
            >
              &raquo;
            </button>
          </div>

          <div className="sso-rd__page-info">
            {t(
              "Displaying page {{page}} of {{total}}. There are {{records}} records in total.",
              { page: page + 1, total: pageCount, records: totalCount }
            )}
          </div>

          <Select
            variant="page"
            value={String(pageSize)}
            onChange={(next) => onPageSizeChange(Number(next))}
            options={PAGE_SIZES.map((size) => ({
              value: String(size),
              label: String(size),
            }))}
            ariaLabel={t("Page size")}
          />
        </div>

        {/* Odaklanabilir `separator` = ARIA'nın pencere bölücüsü; klavyeyle de
            ayarlanabilsin diye ok tuşları bağlandı.
            Kural devre dışı: jsx-a11y `separator`ı hep statik sayıp `aria-valuenow`u
            reddediyor, oysa ARIA'da ODAKLANABİLİR separator için bu değer beklenen
            bilgidir. Kaldırmak lint'i susturur ama bölücüyü eksik bırakır. */}
        {/* eslint-disable-next-line jsx-a11y/role-supports-aria-props */}
        <div
          className="sso-rd__panel-resize"
          role="separator"
          aria-orientation="horizontal"
          aria-label={t("Adjust table height")}
          aria-valuenow={Math.round(currentHeight)}
          aria-valuemin={MIN_PANEL_HEIGHT}
          tabIndex={0}
          onPointerDown={onResizeDown}
          onPointerMove={onResizeMove}
          onPointerUp={onResizeUp}
          onPointerCancel={onResizeUp}
          onKeyDown={onResizeKey}
        />
      </div>

      {/* Kısaltırken belgeyi ayakta tutan pay. Yüksekliği React durumundan
          gelir, sürükleme bitince sıfırlanır ve `is-resizing` kalktığı için
          CSS geçişiyle iner. */}
      <div
        ref={slackRef}
        className={"sso-rd__panel-slack" + (isResizing ? " is-resizing" : "")}
        style={{ height: slack }}
        aria-hidden="true"
      />
    </>
  );
}

/** Kenara olan uzaklığa göre saniyedeki büyüme; kenara yaklaştıkça karesel hızlanır. */
function edgeSpeed(distance: number): number {
  const ratio = (EDGE_ZONE - Math.max(0, distance)) / EDGE_ZONE;
  return EDGE_SPEED_MIN + ratio * ratio * (EDGE_SPEED_MAX - EDGE_SPEED_MIN);
}

function clamp(value: number, max: number): number {
  return Math.min(Math.max(value, MIN_PANEL_HEIGHT), Math.max(max, MIN_PANEL_HEIGHT));
}

/** Sunucuya gidecek filtrenin karşılaştırılabilir hali; boş kolonlar sayılmaz. */
function filterKey(search: string, columns: Record<string, string>): string {
  const parts = Object.keys(columns)
    .filter((name) => columns[name])
    .sort()
    .map((name) => name + "=" + columns[name]);
  return search + "|" + parts.join("&");
}

function sortField<T>(
  column: DataTableColumn<T> | undefined,
  fallback: string
): string | string[] {
  const sortable = column?.sortable;
  if (typeof sortable === "string") return sortable;
  if (Array.isArray(sortable)) return sortable;
  return fallback;
}

/** Aktif sayfanın çevresinde pencere; ilk ve son sayfa hep görünür, `null` = üç nokta. */
function buildPageList(current: number, count: number): (number | null)[] {
  if (count <= 7) {
    return Array.from({ length: count }, (_, i) => i + 1);
  }

  const result: (number | null)[] = [1];
  const start = Math.max(2, current - 1);
  const end = Math.min(count - 1, current + 1);

  if (start > 2) result.push(null);
  for (let i = start; i <= end; i += 1) result.push(i);
  if (end < count - 1) result.push(null);

  result.push(count);
  return result;
}

export default DataTable;
